import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Building2,
  CheckCircle,
  AlertTriangle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  MapPin,
  ListChecks,
  Tag,
  DollarSign,
  Users,
  Layers,
  Clock,
  Compass,
  FileText,
  Info,
  ArrowRight,
  Check,
  Search,
  Navigation,
  Image as ImageIcon,
  Calendar,
  Eye,
  X,
  RefreshCw,
} from "lucide-react";
import Lottie from "lottie-react";
import federationService from "../../../../services/host-apiServices/federationService";
import billingService from "../../../../services/billingService";
import toastUtils from "../../../../utils/toast";
import { useCountries } from "../../../../hooks/useCountries";
import TimezoneSelect from "../../../../components/TimezoneSelect";
import GooglePlacesSearchInput from "../../host-add-space/components/GooglePlacesSearchInput";
import LocationPickerModal from "../../host-add-space/components/LocationPickerModal";
import { Input } from "../../../../shadcn/ui/input";
import { Label } from "../../../../shadcn/ui/label";
import { Textarea } from "../../../../shadcn/ui/textarea";
import { Badge } from "../../../../shadcn/ui/badge";
import { Button } from "../../../../shadcn/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../../shadcn/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../../../../shadcn/ui/select";

// Fallback in case categories have not loaded yet from the server
const FALLBACK_CATEGORIES = [
  {
    id: 1,
    name: "Work",
    subcategories: [
      { id: 1, name: "Private office", categoryId: 1, categoryName: "Work" },
      { id: 4, name: "Meeting Room", categoryId: 1, categoryName: "Work" },
      { id: 5, name: "Dedicated Desk", categoryId: 1, categoryName: "Work" },
      { id: 6, name: "Co-working Space", categoryId: 1, categoryName: "Work" },
    ],
  },
  {
    id: 2,
    name: "Stay",
    subcategories: [
      { id: 2, name: "Accommodation", categoryId: 2, categoryName: "Stay" },
      { id: 7, name: "Bed & Breakfast", categoryId: 2, categoryName: "Stay" },
      { id: 8, name: "Serviced Apartment", categoryId: 2, categoryName: "Stay" },
    ],
  },
  {
    id: 3,
    name: "Play",
    subcategories: [
      { id: 9, name: "Event", categoryId: 3, categoryName: "Play" },
      { id: 10, name: "Dining", categoryId: 3, categoryName: "Play" },
      { id: 11, name: "Wellness", categoryId: 3, categoryName: "Play" },
      { id: 12, name: "Adventure", categoryId: 3, categoryName: "Play" },
    ],
  },
  {
    id: 10,
    name: "Move",
    subcategories: [
      { id: 23, name: "Rent a Vehicle", categoryId: 10, categoryName: "Move" },
      { id: 24, name: "Book a Ride", categoryId: 10, categoryName: "Move" },
    ],
  },
];

const CURRENCIES = ["GHS", "NGN", "KES", "ZAR", "USD", "EUR", "GBP"];

export const isItemApproved = item => {
  if (!item) return false;
  const status = String(item.status || "").toUpperCase();
  return (
    status === "APPROVED" ||
    status === "PROMOTED" ||
    item.promotedBuildingId != null ||
    item.promotedSpaceId != null
  );
};

export const resolveCountryCode = (countryNameOrCode, countryCode, countriesList = []) => {
  const code = String(countryCode || "").trim().toUpperCase();
  const raw = String(countryNameOrCode || "").trim();
  const rawUpper = raw.toUpperCase();
  const rawLower = raw.toLowerCase();

  if (Array.isArray(countriesList) && countriesList.length > 0) {
    const match = countriesList.find(c => {
      const cCode = String(c.code || c.iso_code || c.iso2 || "").trim().toUpperCase();
      const cName = String(c.name || "").trim().toLowerCase();
      return (
        (code && cCode === code) ||
        (rawUpper && cCode === rawUpper) ||
        (rawLower && cName === rawLower)
      );
    });
    if (match) {
      return match.code || match.iso_code || match.iso2 || match.name;
    }
  }

  return code || raw || "";
};

const VALIATION_LABELS = {
  MISSING_SUBCATEGORY: "Subcategory required",
  MISSING_PRICING: "Pricing required",
  MISSING_CURRENCY: "Currency required",
  MISSING_NAME: "Property name required",
  MISSING_ADDRESS: "Street address and city required",
  MISSING_TIMEZONE: "Timezone required",
  MISSING_GPS: "Property location pin on map required",
};

const StagingReview = ({ bindingId, direction, justActivated = false, onBackToWires }) => {
  const reduxCategories = useSelector(state => state.dashboard?.serviceCategories || []);
  const reduxSubcategories = useSelector(state => state.dashboard?.serviceSubcategories || []);
  const [remoteCategories, setRemoteCategories] = useState([]);

  // Fetch all service categories dynamically if not already loaded in Redux
  useEffect(() => {
    if (reduxCategories.length === 0 && reduxSubcategories.length === 0) {
      billingService
        .getAllServiceCategories({ type: "END_USER" })
        .then(res => {
          if (res?.data?.data && Array.isArray(res.data.data)) {
            setRemoteCategories(res.data.data);
          }
        })
        .catch(() => {});
    }
  }, [reduxCategories.length, reduxSubcategories.length]);

  // Unified structured categories with subcategories
  const structuredCategories = useMemo(() => {
    // 1. If remoteCategories loaded directly
    if (remoteCategories.length > 0) {
      return remoteCategories;
    }

    // 2. If reduxCategories have nested subcategories
    const hasNested = reduxCategories.some(c => c.subcategories && c.subcategories.length > 0);
    if (hasNested) {
      return reduxCategories;
    }

    // 3. If reduxCategories and reduxSubcategories are in separate flat stores
    if (reduxCategories.length > 0 && reduxSubcategories.length > 0) {
      return reduxCategories.map(cat => ({
        ...cat,
        subcategories: reduxSubcategories.filter(sub => sub.categoryId === cat.id),
      }));
    }

    // 4. Fallback to default catalog
    return FALLBACK_CATEGORIES;
  }, [remoteCategories, reduxCategories, reduxSubcategories]);

  // Flattened subcategory map for instant O(1) lookup: id -> { id, name, categoryId, categoryName }
  const subcategoryMap = useMemo(() => {
    const map = new Map();
    structuredCategories.forEach(cat => {
      (cat.subcategories || []).forEach(sub => {
        map.set(Number(sub.id), {
          id: Number(sub.id),
          name: sub.name,
          categoryId: cat.id,
          categoryName: cat.name,
        });
      });
    });
    return map;
  }, [structuredCategories]);

  // Helper to get subcategory details by id
  const getSubcategoryById = catId => {
    if (!catId) return null;
    return subcategoryMap.get(Number(catId)) || null;
  };

  const [buildings, setBuildings] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("buildings"); // "buildings" | "spaces"
  const [selectedBuildingId, setSelectedBuildingId] = useState(null);
  const [selectedSpaceId, setSelectedSpaceId] = useState(null);
  const [filterMode, setFilterMode] = useState("all"); // "all" | "pending" | "approved"
  // When on the Spaces tab, further filter by a specific staged building id.
  // null = show all spaces; a number = only spaces belonging to that building.
  const [spaceBuildingFilter, setSpaceBuildingFilter] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);

  const [buildingEdits, setBuildingEdits] = useState({});
  const [spaceEdits, setSpaceEdits] = useState({});
  const [saving, setSaving] = useState({});

  const [approvingAll, setApprovingAll] = useState(false);
  const [rebackfilling, setRebackfilling] = useState(false);
  // Vendor changes recorded against ALREADY-PROMOTED items. Distinct from the staged lists above:
  // a promoted space is never updated silently by a vendor delta, so the change waits here until an
  // operator applies it.
  const [pendingUpdates, setPendingUpdates] = useState([]);
  const [applyingUpdates, setApplyingUpdates] = useState(false);
  const [showRebackfillModal, setShowRebackfillModal] = useState(false);
  const [publishOnApprove, setPublishOnApprove] = useState(true);
  const [lottieData, setLottieData] = useState(null);
  const [lottieDone, setLottieDone] = useState(false);
  const [stillPulling, setStillPulling] = useState(justActivated);

  useEffect(() => {
    fetch("/assets/animations/spaces-pulled.json")
      .then(res => (res.ok ? res.json() : null))
      .then(data => setLottieData(data))
      .catch(() => setLottieData(null));
  }, []);

  const fetchSpaces = async (bId = spaceBuildingFilter) => {
    try {
      const targetBuildingId = bId !== null && bId !== undefined && bId !== "" ? Number(bId) : undefined;
      const s = await federationService.listStagedSpaces(bindingId, targetBuildingId);
      const spaceList = Array.isArray(s) ? s : [];
      setSpaces(spaceList);
      if (spaceList.length > 0) {
        setSelectedSpaceId(prev => (spaceList.some(item => item.id === prev) ? prev : spaceList[0].id));
      } else {
        setSelectedSpaceId(null);
      }
    } catch {
      setSpaces([]);
    }
  };

  const fetchStaged = async () => {
    try {
      const targetBuildingId = spaceBuildingFilter !== null && spaceBuildingFilter !== undefined && spaceBuildingFilter !== "" ? Number(spaceBuildingFilter) : undefined;
      const [b, s] = await Promise.all([
        federationService.listStagedBuildings(bindingId),
        federationService.listStagedSpaces(bindingId, targetBuildingId),
      ]);
      const buildingList = Array.isArray(b) ? b : [];
      const spaceList = Array.isArray(s) ? s : [];
      setBuildings(buildingList);
      setSpaces(spaceList);

      if (buildingList.length > 0 && !selectedBuildingId) {
        const pendingBuilding = buildingList.find(x => !isItemApproved(x)) || buildingList[0];
        setSelectedBuildingId(pendingBuilding.id);
      }
      if (spaceList.length > 0 && !selectedSpaceId) {
        const pendingSpace = spaceList.find(x => !isItemApproved(x)) || spaceList[0];
        setSelectedSpaceId(pendingSpace.id);
      }
    } catch {
      setBuildings([]);
      setSpaces([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBuilding = buildingId => {
    setSelectedBuildingId(buildingId);
    setSpaceBuildingFilter(buildingId);
    fetchSpaces(buildingId);
  };

  const fetchPendingUpdates = useCallback(async () => {
    try {
      const all = await federationService.listPendingUpdates();
      // The endpoint is org-scoped, not binding-scoped — this dialog is about one binding.
      setPendingUpdates((all || []).filter(p => !bindingId || p.bindingId === bindingId));
    } catch (err) {
      // Non-fatal: the review surface is additive, and an older API simply has no such endpoint.
      setPendingUpdates([]);
    }
  }, [bindingId]);

  useEffect(() => {
    if (direction === "CONSUME") {
      fetchStaged();
      fetchPendingUpdates();
    }
  }, [bindingId, direction, fetchPendingUpdates]);

  useEffect(() => {
    if (!stillPulling) return;
    let attempts = 0;
    const MAX_ATTEMPTS = 15;
    const interval = setInterval(async () => {
      attempts += 1;
      try {
        const [b, s] = await Promise.all([
          federationService.listStagedBuildings(),
          federationService.listStagedSpaces(),
        ]);
        const buildingList = Array.isArray(b) ? b : [];
        const spaceList = Array.isArray(s) ? s : [];
        setBuildings(buildingList);
        setSpaces(spaceList);

        if (buildingList.length > 0 && !selectedBuildingId) {
          const pendingBuilding = buildingList.find(x => !isItemApproved(x)) || buildingList[0];
          setSelectedBuildingId(pendingBuilding.id);
        }
        if (spaceList.length > 0 && !selectedSpaceId) {
          const pendingSpace = spaceList.find(x => !isItemApproved(x)) || spaceList[0];
          setSelectedSpaceId(pendingSpace.id);
        }

        if ((b?.length || s?.length) || attempts >= MAX_ATTEMPTS) {
          clearInterval(interval);
          setStillPulling(false);
        }
      } catch {
        if (attempts >= MAX_ATTEMPTS) {
          clearInterval(interval);
          setStillPulling(false);
        }
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [stillPulling, selectedBuildingId, selectedSpaceId]);

  const totalPending = useMemo(
    () => buildings.filter(b => !isItemApproved(b)).length + spaces.filter(s => !isItemApproved(s)).length,
    [buildings, spaces]
  );
  const hasItems = useMemo(() => buildings.length > 0 || spaces.length > 0, [buildings, spaces]);

  const promotablePending = useMemo(() => {
    const validBuildingsCount = buildings.filter(b => {
      if (isItemApproved(b)) return false;
      const edits = buildingEdits[b.id] || {};
      const name = edits.buildingName !== undefined ? edits.buildingName : (b.buildingName || b.name);
      const street = edits.streetAddress !== undefined ? edits.streetAddress : b.streetAddress;
      const city = edits.city !== undefined ? edits.city : b.city;
      const timezone = edits.timezone !== undefined ? edits.timezone : b.timezone;
      const lat = edits.latitude !== undefined ? edits.latitude : b.latitude;
      const lng = edits.longitude !== undefined ? edits.longitude : b.longitude;
      const hasGps = lat !== null && lat !== undefined && lng !== null && lng !== undefined && (Number(lat) !== 0 || Number(lng) !== 0);

      const hasName = Boolean(name && String(name).trim() !== "");
      const hasAddress = Boolean((street && String(street).trim() !== "") || (city && String(city).trim() !== ""));
      const hasTz = Boolean(timezone && String(timezone).trim() !== "");

      return (hasName && hasAddress && hasTz && hasGps) || b.promotable === true;
    }).length;

    const validSpacesCount = spaces.filter(s => {
      if (isItemApproved(s)) return false;
      const edits = spaceEdits[s.id] || {};
      const subCat = edits.subCategoryId !== undefined ? edits.subCategoryId : s.subCategoryId;
      const price = edits.ratePrice !== undefined ? edits.ratePrice : s.ratePrice;
      const curr = edits.currency !== undefined ? edits.currency : s.currency;
      const hasSubCat = Boolean(subCat && Number(subCat) > 0);
      const hasPrice = price !== undefined && price !== null && price !== "" && Number(price) >= 0;
      const hasCurr = Boolean(curr && String(curr).trim() !== "");
      return (hasSubCat && hasPrice && hasCurr) || s.promotable === true;
    }).length;

    return validBuildingsCount + validSpacesCount;
  }, [buildings, spaces, buildingEdits, spaceEdits]);

  useEffect(() => {
    if (totalPending === 0 && hasItems) {
      const timer = setTimeout(() => setLottieDone(true), 4000);
      return () => clearTimeout(timer);
    }
  }, [totalPending, hasItems]);

  // Countries list for illustrative country select
  const { countries } = useCountries();

  const handleBuildingEdit = (buildingId, field, value) => {
    setBuildingEdits(prev => ({
      ...prev,
      [buildingId]: {
        ...prev[buildingId],
        [field]: value,
      },
    }));
  };

  const handleBuildingBatchEdit = (buildingId, updates) => {
    setBuildingEdits(prev => ({
      ...prev,
      [buildingId]: {
        ...prev[buildingId],
        ...updates,
      },
    }));
  };

  const handleSaveBuilding = async buildingId => {
    const payload = buildingEdits[buildingId];
    if (!payload) return;
    setSaving(prev => ({ ...prev, [`building_${buildingId}`]: true }));
    try {
      await federationService.updateStagedItem("buildings", buildingId, payload);
      toastUtils.success("Building details saved");
      setBuildingEdits(prev => {
        const r = { ...prev };
        delete r[buildingId];
        return r;
      });
      const updatedBuildings = await federationService.listStagedBuildings();
      setBuildings(Array.isArray(updatedBuildings) ? updatedBuildings : []);
    } catch {
      toastUtils.error("Failed to update building");
    } finally {
      setSaving(prev => ({ ...prev, [`building_${buildingId}`]: false }));
    }
  };

  const handleSpaceEdit = (spaceId, field, value) => {
    setSpaceEdits(prev => ({
      ...prev,
      [spaceId]: {
        ...prev[spaceId],
        [field]: value,
      },
    }));
  };

  const handleSaveSpace = async spaceId => {
    const payload = spaceEdits[spaceId];
    if (!payload) return;
    setSaving(prev => ({ ...prev, [`space_${spaceId}`]: true }));
    try {
      await federationService.updateStagedItem("spaces", spaceId, payload);
      toastUtils.success("Space details saved");
      setSpaceEdits(prev => {
        const r = { ...prev };
        delete r[spaceId];
        return r;
      });
      const updatedSpaces = await federationService.listStagedSpaces();
      setSpaces(Array.isArray(updatedSpaces) ? updatedSpaces : []);
    } catch {
      toastUtils.error("Failed to update space");
    } finally {
      setSaving(prev => ({ ...prev, [`space_${spaceId}`]: false }));
    }
  };

  const buildPublishPayload = () => ({
    publish: publishOnApprove,
    visibility: publishOnApprove ? "PUBLIC" : "PRIVATE",
  });

  const handleApproveBuilding = async (id, extraPayload) => {
    try {
      await federationService.approveStagedItem("buildings", id, extraPayload);
      toastUtils.success("Building approved and synced");
      setBuildingEdits(prev => {
        const r = { ...prev };
        delete r[id];
        return r;
      });
      await fetchStaged();
    } catch (err) {
      toastUtils.error(err?.response?.data?.message || "Failed to approve building");
    }
  };

  const handleApproveSpace = async (id, extraPayload) => {
    try {
      await federationService.approveStagedItem("spaces", id, { ...buildPublishPayload(), ...extraPayload });
      toastUtils.success("Space approved and published");
      setSpaceEdits(prev => {
        const r = { ...prev };
        delete r[id];
        return r;
      });
      await fetchStaged();
    } catch (err) {
      toastUtils.error(err?.response?.data?.message || "Failed to approve space");
    }
  };

  const handleApproveBuildingWithAutoSave = async id => {
    const pendingEdits = buildingEdits[id];
    if (pendingEdits) {
      try {
        await federationService.updateStagedItem("buildings", id, pendingEdits);
        setBuildingEdits(prev => {
          const r = { ...prev };
          delete r[id];
          return r;
        });
      } catch (err) {
        toastUtils.error("Failed to save building edits before approving");
        return;
      }
    }
    await handleApproveBuilding(id, pendingEdits);
  };

  const handleApproveSpaceWithAutoSave = async id => {
    const pendingEdits = spaceEdits[id];
    if (pendingEdits) {
      try {
        await federationService.updateStagedItem("spaces", id, pendingEdits);
        setSpaceEdits(prev => {
          const r = { ...prev };
          delete r[id];
          return r;
        });
      } catch (err) {
        toastUtils.error("Failed to save space edits before approving");
        return;
      }
    }
    await handleApproveSpace(id, pendingEdits);
  };

  const handleApproveAll = async () => {
    setApprovingAll(true);
    const pendingBuildings = buildings.filter(b => !isItemApproved(b) && b.promotable);
    const pendingSpaces = spaces.filter(s => !isItemApproved(s) && s.promotable);
    const total = pendingBuildings.length + pendingSpaces.length;
    if (!total) {
      setApprovingAll(false);
      return;
    }
    let ok = 0;
    let fail = 0;
    const publishPayload = buildPublishPayload();
    for (const b of pendingBuildings) {
      try {
        await federationService.approveStagedItem("buildings", b.id, publishPayload);
        ok++;
      } catch {
        fail++;
      }
    }
    for (const s of pendingSpaces) {
      try {
        await federationService.approveStagedItem("spaces", s.id, publishPayload);
        ok++;
      } catch {
        fail++;
      }
    }
    setApprovingAll(false);
    if (fail === 0) {
      toastUtils.success(`Approved all ${ok} items`);
    } else {
      toastUtils.success(`Approved ${ok} item(s)`);
      toastUtils.error(`${fail} item(s) failed`);
    }
    await fetchStaged();
  };

  const handleRebackfill = async () => {
    setRebackfilling(true);
    try {
      await federationService.rebackfillBinding(bindingId);
      // Load-bearing, not a health check: re-backfill resets the binding to ACTIVATING, and
      // completeActivation refuses to finish unless there is a green connection test inside the
      // activation window. Without this the walk finishes and the binding sits in ACTIVATING forever.
      await federationService.testBinding(bindingId);
      setShowRebackfillModal(false);
      toastUtils.success("Re-fetching from host", {
        description: "Reading the host's catalogue again. This usually takes a few seconds.",
      });

      // Wait for the backfill to actually finish rather than guessing with a timeout. It leaves
      // ACTIVATING on success OR on failure, so check which — a vendor that is unhealthy parks the
      // binding in ERROR, and silently showing stale data would look like "nothing changed".
      // 4 minutes: the sweep walks one vendor page per tick, so a large aggregator catalogue takes
      // far longer than a single-property PMS. Timing out only downgrades the message.
      const binding = await federationService.waitForBackfill(bindingId, 240000);
      if (binding?.status === "ERROR") {
        toastUtils.error("Re-fetch did not complete", {
          description: "The host could not be read fully. The connection is in an error state.",
        });
      } else if (binding?.status === "ACTIVATING") {
        toastUtils.error("Re-fetch is taking longer than expected", {
          description: "It is still running — reopen this dialog shortly to see the result.",
        });
      }

      await fetchStaged();
      await fetchPendingUpdates();
    } catch (err) {
      toastUtils.error(err?.response?.data?.message || "Failed to re-fetch spaces");
    } finally {
      setRebackfilling(false);
    }
  };

  const handleApplyPendingUpdates = async () => {
    setApplyingUpdates(true);
    try {
      const spaceIds = pendingUpdates.filter(p => p.localEntityType === "SPACE").map(p => p.localId);
      const buildingIds = pendingUpdates.filter(p => p.localEntityType === "BUILDING").map(p => p.localId);
      const result = await federationService.resyncMany(spaceIds, buildingIds);

      const applied =
        (result?.appliedSpaceIds?.length || 0) + (result?.appliedBuildingIds?.length || 0);
      const failed = Object.keys(result?.failures || {}).length;

      if (applied > 0) {
        toastUtils.success(`Applied ${applied} host update${applied === 1 ? "" : "s"}`);
      }
      // Partial success is normal — another operator may have applied one first — so report it
      // rather than letting a silent no-op read as success.
      if (failed > 0) {
        toastUtils.error(`${failed} update${failed === 1 ? "" : "s"} could not be applied`);
      }
      await fetchStaged();
      await fetchPendingUpdates();
    } catch (err) {
      toastUtils.error(err?.response?.data?.message || "Failed to apply host updates");
    } finally {
      setApplyingUpdates(false);
    }
  };

  const parseFlags = flags => (flags || "").split(",").filter(Boolean);

  // Filtered lists for sidebar
  const filteredBuildings = useMemo(() => {
    if (filterMode === "pending") return buildings.filter(b => !isItemApproved(b));
    if (filterMode === "approved") return buildings.filter(b => isItemApproved(b));
    return buildings;
  }, [buildings, filterMode]);

  const filteredSpaces = useMemo(() => {
    let list = spaces;
    // Filter by building (matches stagedBuildingId on the space object)
    if (spaceBuildingFilter !== null) {
      list = list.filter(s => s.stagedBuildingId === spaceBuildingFilter || s.buildingId === spaceBuildingFilter);
    }
    if (filterMode === "pending") return list.filter(s => !isItemApproved(s));
    if (filterMode === "approved") return list.filter(s => isItemApproved(s));
    return list;
  }, [spaces, filterMode, spaceBuildingFilter]);

  // Active items
  const activeBuilding = useMemo(() => buildings.find(b => b.id === selectedBuildingId), [buildings, selectedBuildingId]);
  const activeSpace = useMemo(() => spaces.find(s => s.id === selectedSpaceId), [spaces, selectedSpaceId]);

  const hasBuildingChanges = activeBuilding && Object.keys(buildingEdits[activeBuilding.id] || {}).length > 0;
  const hasSpaceChanges = activeSpace && Object.keys(spaceEdits[activeSpace.id] || {}).length > 0;

  // Selected space subcategory helper
  const activeSubcategory = useMemo(() => {
    if (!activeSpace) return null;
    const catId = spaceEdits[activeSpace.id]?.subCategoryId ?? activeSpace.subCategoryId;
    return getSubcategoryById(catId);
  }, [activeSpace, spaceEdits, subcategoryMap]);

  // Next item navigation helpers.
  //
  // These walk the FILTERED lists, not the raw ones. The sidebar shows filteredSpaces — scoped to the
  // selected building and to the pending/approved filter — so indexing `spaces` could jump to a space
  // that is not on screen and, at the end of a building, leave the button live with nowhere to go.
  const nextSpace = useMemo(() => {
    const idx = filteredSpaces.findIndex(s => s.id === selectedSpaceId);
    // Selected space is no longer in view — it was just approved under the "pending" filter, or the
    // building filter moved. The next thing to review is the first one still listed, not "none".
    if (idx === -1) return filteredSpaces[0] || null;
    return filteredSpaces[idx + 1] || null;
  }, [filteredSpaces, selectedSpaceId]);

  const handleSelectNext = () => {
    if (tab === "buildings") {
      const idx = filteredBuildings.findIndex(b => b.id === selectedBuildingId);
      const next = idx === -1 ? filteredBuildings[0] : filteredBuildings[idx + 1];
      if (next) {
        setSelectedBuildingId(next.id);
      } else {
        setTab("spaces");
      }
    } else if (nextSpace) {
      setSelectedSpaceId(nextSpace.id);
    }
  };

  // Helper to dynamically calculate pending validation flags for a space incorporating user edits
  const getDynamicSpaceFlags = space => {
    if (!space || isItemApproved(space)) return [];
    const edits = spaceEdits[space.id] || {};
    const effectiveSubCategory = edits.subCategoryId !== undefined ? edits.subCategoryId : space.subCategoryId;
    const effectivePrice = edits.ratePrice !== undefined ? edits.ratePrice : space.ratePrice;
    const effectiveCurrency = edits.currency !== undefined ? edits.currency : space.currency;

    const flags = [];
    if (!effectiveSubCategory || Number(effectiveSubCategory) === 0) {
      flags.push("MISSING_SUBCATEGORY");
    }
    if (effectivePrice === undefined || effectivePrice === null || effectivePrice === "" || Number(effectivePrice) < 0) {
      flags.push("MISSING_PRICING");
    }
    if (!effectiveCurrency || String(effectiveCurrency).trim() === "") {
      flags.push("MISSING_CURRENCY");
    }
    return flags;
  };

  const getDynamicBuildingFlags = building => {
    if (!building || isItemApproved(building)) return [];
    const edits = buildingEdits[building.id] || {};
    const effectiveName = edits.buildingName !== undefined ? edits.buildingName : (building.buildingName || building.name);
    const effectiveStreet = edits.streetAddress !== undefined ? edits.streetAddress : building.streetAddress;
    const effectiveCity = edits.city !== undefined ? edits.city : building.city;
    const effectiveTimezone = edits.timezone !== undefined ? edits.timezone : building.timezone;
    const effectiveLat = edits.latitude !== undefined ? edits.latitude : building.latitude;
    const effectiveLng = edits.longitude !== undefined ? edits.longitude : building.longitude;

    const flags = [];
    if (!effectiveName || String(effectiveName).trim() === "") {
      flags.push("MISSING_NAME");
    }
    if ((!effectiveStreet || String(effectiveStreet).trim() === "") && (!effectiveCity || String(effectiveCity).trim() === "")) {
      flags.push("MISSING_ADDRESS");
    }
    if (!effectiveTimezone || String(effectiveTimezone).trim() === "") {
      flags.push("MISSING_TIMEZONE");
    }
    const hasGps =
      effectiveLat !== null &&
      effectiveLat !== undefined &&
      effectiveLng !== null &&
      effectiveLng !== undefined &&
      (Number(effectiveLat) !== 0 || Number(effectiveLng) !== 0);
    if (!hasGps) {
      flags.push("MISSING_GPS");
    }
    return flags;
  };

  // Dynamic promotability: if user filled the required fields (or if backend marked promotable), approve is enabled!
  const isSpacePromotable = useMemo(() => {
    if (!activeSpace) return false;
    const dynamicFlags = getDynamicSpaceFlags(activeSpace);
    return dynamicFlags.length === 0 || activeSpace.promotable === true;
  }, [activeSpace, spaceEdits]);

  const spaceFlags = useMemo(() => {
    return getDynamicSpaceFlags(activeSpace);
  }, [activeSpace, spaceEdits]);

  const isBuildingPromotable = useMemo(() => {
    if (!activeBuilding) return false;
    const dynamicFlags = getDynamicBuildingFlags(activeBuilding);
    return dynamicFlags.length === 0 || activeBuilding.promotable === true;
  }, [activeBuilding, buildingEdits]);

  const buildingFlags = useMemo(() => {
    return getDynamicBuildingFlags(activeBuilding);
  }, [activeBuilding, buildingEdits]);

  if (direction !== "CONSUME") return null;

  if (loading) {
    return (
      <div className="p-8 space-y-4 bg-white rounded-2xl border border-slate-100 shadow-sm animate-pulse">
        <div className="h-6 w-48 bg-slate-200 rounded-md" />
        <div className="h-32 bg-slate-100 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col">
      {/* ── Top Header Navigation ────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between border-b border-slate-150 bg-slate-50/70 p-4 gap-4">
        {/* Review Steps */}
        <div className="flex items-center gap-3 md:gap-5 flex-1 flex-wrap">
          {onBackToWires && (
            <button
              type="button"
              onClick={onBackToWires}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-50 shadow-2xs transition-colors shrink-0"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              Settings
            </button>
          )}

          {/* Buildings Tab Button */}
          <button
            type="button"
            onClick={() => setTab("buildings")}
            className={`flex items-center gap-3 px-3 py-2 rounded-xl transition-all outline-none ${
              tab === "buildings"
                ? "bg-white shadow-sm border border-slate-200/80 ring-2 ring-indigo-500/10"
                : "hover:bg-slate-100/70"
            }`}
          >
            <div
              className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold transition-all ${
                tab === "buildings"
                  ? "bg-indigo-600 text-white"
                  : buildings.length > 0 && buildings.every(b => isItemApproved(b))
                  ? "bg-emerald-500 text-white"
                  : "bg-slate-200 text-slate-600"
              }`}
            >
              {buildings.length > 0 && buildings.every(b => isItemApproved(b)) ? (
                <Check className="w-3.5 h-3.5 stroke-[2.5]" />
              ) : (
                "1"
              )}
            </div>
            <div className="text-left">
              <p className={`text-xs font-bold tracking-tight ${tab === "buildings" ? "text-slate-900" : "text-slate-600"}`}>
                Buildings Review
              </p>
              <p className="text-[11px] text-slate-400 font-medium">
                {buildings.filter(b => !isItemApproved(b)).length} pending · {buildings.length} total
              </p>
            </div>
          </button>

          <ChevronRight className="w-4 h-4 text-slate-300 hidden sm:block shrink-0" />

          {/* Spaces Tab Button */}
          <button
            type="button"
            onClick={() => setTab("spaces")}
            className={`flex items-center gap-3 px-3 py-2 rounded-xl transition-all outline-none ${
              tab === "spaces"
                ? "bg-white shadow-sm border border-slate-200/80 ring-2 ring-indigo-500/10"
                : "hover:bg-slate-100/70"
            }`}
          >
            <div
              className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold transition-all ${
                tab === "spaces"
                  ? "bg-indigo-600 text-white"
                  : spaces.length > 0 && spaces.every(s => isItemApproved(s))
                  ? "bg-emerald-500 text-white"
                  : "bg-slate-200 text-slate-600"
              }`}
            >
              {spaces.length > 0 && spaces.every(s => isItemApproved(s)) ? (
                <Check className="w-3.5 h-3.5 stroke-[2.5]" />
              ) : (
                "2"
              )}
            </div>
            <div className="text-left">
              <p className={`text-xs font-bold tracking-tight ${tab === "spaces" ? "text-slate-900" : "text-slate-600"}`}>
                Spaces Review
              </p>
              <p className="text-[11px] text-slate-400 font-medium">
                {spaces.filter(s => !isItemApproved(s)).length} pending · {spaces.length} total
              </p>
            </div>
          </button>
        </div>

        {/* Global Bulk Actions */}
        <div className="flex items-center gap-2 self-end sm:self-auto">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowRebackfillModal(true)}
            disabled={rebackfilling}
            className="text-xs h-8 text-slate-600 border-slate-200"
          >
            {rebackfilling ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
            )}
            {rebackfilling ? "Refreshing..." : "Re-fetch from host"}
          </Button>
          {promotablePending > 0 && (
            <Button
              type="button"
              size="sm"
              onClick={handleApproveAll}
              disabled={approvingAll}
              className="text-xs h-8 bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              {approvingAll ? `Approving ${promotablePending}...` : `Approve all ready (${promotablePending})`}
            </Button>
          )}
        </div>
      </div>

      {/* ── Host updates to already-published spaces ──────────────────
          Separate from the staged lists: these items are already live on Spacia, and the host has
          since changed them. Nothing is applied until the operator says so. */}
      {pendingUpdates.length > 0 && (
        <div className="mx-4 mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-2.5">
              <RefreshCw className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
              <div>
                <p className="text-sm font-bold text-amber-900">
                  {pendingUpdates.length} published{" "}
                  {pendingUpdates.length === 1 ? "space has" : "spaces have"} changes from the host
                </p>
                <p className="text-xs text-amber-800 mt-0.5">
                  {pendingUpdates
                    .slice(0, 3)
                    .map(u => {
                      const fields = (u.changedFields || []).join(", ");
                      return fields ? `${u.localName} (${fields})` : u.localName;
                    })
                    .join(" · ")}
                  {pendingUpdates.length > 3 && ` · +${pendingUpdates.length - 3} more`}
                </p>
              </div>
            </div>
            <Button
              type="button"
              size="sm"
              onClick={handleApplyPendingUpdates}
              disabled={applyingUpdates}
              className="text-xs h-8 shrink-0 bg-amber-600 hover:bg-amber-700 text-white shadow-sm"
            >
              {applyingUpdates ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <Check className="w-3.5 h-3.5 mr-1.5" />
              )}
              {applyingUpdates ? "Applying..." : `Apply ${pendingUpdates.length} update${pendingUpdates.length === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      )}

      {/* ── Async Staging Lottie / Loading State ───────────────────── */}
      {stillPulling && !hasItems && (
        <div className="mx-4 my-8 flex flex-col items-center py-14 text-center">
          {lottieData ? (
            <Lottie animationData={lottieData} loop className="w-28 h-28" />
          ) : (
            <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
          )}
          <p className="text-sm font-bold text-slate-800 mt-4">Pulling spaces from the host platform…</p>
          <p className="text-xs text-slate-500 mt-1 max-w-sm">
            Imported inventory is staging in Spacia. This will populate in a few seconds.
          </p>
        </div>
      )}

      {totalPending === 0 && hasItems && (
        <div className="mx-4 mt-4 flex flex-col items-center overflow-hidden">
          <div
            className={`transition-all duration-500 ease-in-out flex flex-col items-center ${
              lottieDone ? "opacity-0 scale-75 max-h-0 py-0" : "opacity-100 scale-100 max-h-48 py-6"
            }`}
          >
            {lottieData ? (
              <Lottie animationData={lottieData} loop={false} className="w-24 h-24" onComplete={() => setLottieDone(true)} />
            ) : (
              <CheckCircle className="w-10 h-10 text-emerald-500 animate-bounce" />
            )}
            <p className="text-sm font-bold text-emerald-700 mt-2">All inventory approved and live!</p>
          </div>
        </div>
      )}

      {/* ── Main Split View ────────────────────────────────────────── */}
      {stillPulling && !hasItems ? null : (
        <div className="grid grid-cols-1 md:grid-cols-12 min-h-[620px] bg-slate-50/30">
          {/* ── LEFT COLUMN: INVENTORY LIST ─────────────────────────── */}
          <div className="md:col-span-5 lg:col-span-4 border-r border-slate-200/80 bg-white flex flex-col">
            {/* Filter pills bar */}
            <div className="p-3 border-b border-slate-100 flex items-center justify-between gap-1 bg-slate-50/50">
              <div className="flex items-center gap-1">
                {[
                  { id: "all", label: "All" },
                  { id: "pending", label: "Pending" },
                  { id: "approved", label: "Approved" },
                ].map(f => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setFilterMode(f.id)}
                    className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-all ${
                      filterMode === f.id
                        ? "bg-white text-slate-900 shadow-sm border border-slate-200/60"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <span className="text-[11px] font-semibold text-slate-400">
                {tab === "buildings" ? filteredBuildings.length : filteredSpaces.length} item{filteredSpaces.length !== 1 ? "s" : ""}
              </span>
            </div>

            {/* Building filter — shown only on Spaces tab when there is more than one building */}
            {tab === "spaces" && buildings.length > 0 && (
              <div className="px-3 py-2 border-b border-slate-100 bg-white flex items-center gap-2">
                <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <select
                  value={spaceBuildingFilter ?? ""}
                  onChange={e => {
                    const val = e.target.value;
                    const nextVal = val === "" ? null : Number(val);
                    setSpaceBuildingFilter(nextVal);
                    fetchSpaces(nextVal);
                  }}
                  className="flex-1 text-[11px] font-medium text-slate-700 bg-transparent border-none outline-none cursor-pointer py-0.5 truncate"
                  aria-label="Filter spaces by building"
                >
                  <option value="">All buildings</option>
                  {buildings.map(b => {
                    const name = b.buildingName || b.name || `Building #${b.id}`;
                    const isApproved = isItemApproved(b);
                    const spaceCount = spaces.filter(s => s.stagedBuildingId === b.id || s.buildingId === b.id).length;
                    return (
                      <option key={b.id} value={b.id}>
                        {name}{!isApproved ? " ⚠" : ""} ({spaceCount} space{spaceCount !== 1 ? "s" : ""})
                      </option>
                    );
                  })}
                </select>
                {spaceBuildingFilter !== null && (
                  <button
                    type="button"
                    onClick={() => {
                      setSpaceBuildingFilter(null);
                      fetchSpaces(null);
                    }}
                    className="text-slate-400 hover:text-slate-700 shrink-0"
                    aria-label="Clear building filter"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}

            {/* List scroll area */}
            <div className="flex-1 overflow-y-auto max-h-[640px] divide-y divide-slate-100">
              {tab === "buildings" ? (
                !filteredBuildings.length ? (
                  <div className="p-10 text-center text-xs text-slate-400 font-medium">No buildings found</div>
                ) : (
                  filteredBuildings.map(b => {
                    const isSelected = selectedBuildingId === b.id;
                    const flags = getDynamicBuildingFlags(b);
                    const isApproved = isItemApproved(b);
                    const isPendingAttention = flags.length > 0;
                    const effectiveBuildingName = buildingEdits[b.id]?.buildingName !== undefined ? buildingEdits[b.id]?.buildingName : (b.buildingName || b.name);

                    return (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => handleSelectBuilding(b.id)}
                        className={`w-full text-left p-3.5 transition-all flex items-start gap-3 outline-none group border-l-4 ${
                          isSelected
                            ? "bg-indigo-50/40 border-indigo-600 shadow-sm"
                            : "border-transparent hover:bg-slate-50/80"
                        }`}
                      >
                        <div
                          className={`p-2.5 rounded-xl shrink-0 transition-transform group-hover:scale-105 ${
                            isApproved
                              ? "bg-emerald-50 text-emerald-600 border border-emerald-100"
                              : isPendingAttention
                              ? "bg-amber-50 text-amber-600 border border-amber-200"
                              : "bg-slate-100 text-slate-600 border border-slate-200"
                          }`}
                        >
                          <Building2 className="w-4 h-4" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1.5 mb-0.5">
                            <p className="text-xs font-bold text-slate-900 truncate">
                              {effectiveBuildingName || `Building #${b.id}`}
                            </p>
                            {isApproved ? (
                              <Badge variant="secondary" className="text-[9px] px-1.5 py-0 bg-emerald-50 text-emerald-700 border-emerald-200">
                                Approved
                              </Badge>
                            ) : isPendingAttention ? (
                              <Badge variant="secondary" className="text-[9px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200">
                                Fix Needed
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="text-[9px] px-1.5 py-0 bg-indigo-50 text-indigo-700 border-indigo-200">
                                Ready
                              </Badge>
                            )}
                          </div>

                          <p className="text-[11px] text-slate-500 truncate flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                            {b.streetAddress || "Address not provided"}
                            {b.city ? ` · ${b.city}` : ""}
                          </p>

                          {flags.length > 0 && (
                            <div className="flex items-center gap-1.5 mt-1.5">
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 bg-amber-50/80 border border-amber-200/80 rounded-md">
                                <AlertTriangle className="w-2.5 h-2.5 text-amber-500" />
                                {flags.length} field{flags.length !== 1 ? "s" : ""} to complete
                              </span>
                            </div>
                          )}

                          {/* "View spaces →" shortcut — scopes the Spaces tab to this building */}
                          {(() => {
                            const bSpaceCount = spaces.filter(s => s.stagedBuildingId === b.id || s.buildingId === b.id).length;
                            if (bSpaceCount === 0) return null;
                            return (
                              <button
                                type="button"
                                onClick={e => {
                                  e.stopPropagation();
                                  setTab("spaces");
                                  setSpaceBuildingFilter(b.id);
                                  fetchSpaces(b.id);
                                }}
                                className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-600 hover:text-indigo-800 hover:underline"
                              >
                                <Layers className="w-2.5 h-2.5" />
                                {bSpaceCount} space{bSpaceCount !== 1 ? "s" : ""} →
                              </button>
                            );
                          })()}
                        </div>
                      </button>
                    );
                  })
                )
              ) : (
                !filteredSpaces.length ? (
                  <div className="p-10 text-center text-xs text-slate-400 font-medium">No spaces found</div>
                ) : (
                  filteredSpaces.map(s => {
                    const isSelected = selectedSpaceId === s.id;
                    const flags = getDynamicSpaceFlags(s);
                    const isApproved = isItemApproved(s);
                    const isPendingAttention = flags.length > 0;

                    const effectiveRatePrice = spaceEdits[s.id]?.ratePrice !== undefined ? spaceEdits[s.id]?.ratePrice : s.ratePrice;
                    const effectiveCurrency = spaceEdits[s.id]?.currency !== undefined ? spaceEdits[s.id]?.currency : s.currency;
                    const effectiveCapacity = spaceEdits[s.id]?.capacity !== undefined ? spaceEdits[s.id]?.capacity : s.capacity;
                    const effectiveName = spaceEdits[s.id]?.name !== undefined ? spaceEdits[s.id]?.name : s.name;

                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setSelectedSpaceId(s.id)}
                        className={`w-full text-left p-3.5 transition-all flex items-start gap-3 outline-none group border-l-4 ${
                          isSelected
                            ? "bg-indigo-50/40 border-indigo-600 shadow-sm"
                            : "border-transparent hover:bg-slate-50/80"
                        }`}
                      >
                        <div
                          className={`p-2.5 rounded-xl shrink-0 transition-transform group-hover:scale-105 ${
                            isApproved
                              ? "bg-emerald-50 text-emerald-600 border border-emerald-100"
                              : isPendingAttention
                              ? "bg-amber-50 text-amber-600 border border-amber-200"
                              : "bg-slate-100 text-slate-600 border border-slate-200"
                          }`}
                        >
                          <Layers className="w-4 h-4" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1.5 mb-0.5">
                            <p className="text-xs font-bold text-slate-900 truncate">
                              {effectiveName || `Space #${s.id}`}
                            </p>
                            {isApproved ? (
                              <Badge variant="secondary" className="text-[9px] px-1.5 py-0 bg-emerald-50 text-emerald-700 border-emerald-200">
                                Approved
                              </Badge>
                            ) : isPendingAttention ? (
                              <Badge variant="secondary" className="text-[9px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200">
                                Fix Needed
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="text-[9px] px-1.5 py-0 bg-indigo-50 text-indigo-700 border-indigo-200">
                                Ready
                              </Badge>
                            )}
                          </div>

                          <p className="text-[11px] text-slate-500 truncate flex items-center gap-1.5">
                            {(() => {
                              const effSubCatId = spaceEdits[s.id]?.subCategoryId !== undefined ? spaceEdits[s.id]?.subCategoryId : s.subCategoryId;
                              const subInfo = getSubcategoryById(effSubCatId);
                              return (
                                <>
                                  <span className="font-medium text-slate-700">
                                    {subInfo?.name || "Uncategorized"}
                                  </span>
                                  <span>•</span>
                                </>
                              );
                            })()}
                            <span>{effectiveCapacity ? `${effectiveCapacity} seats` : "Seats unspecified"}</span>
                            <span>•</span>
                            <span>
                              {effectiveRatePrice && effectiveCurrency ? `${effectiveCurrency} ${effectiveRatePrice}` : "Rate unpriced"}
                            </span>
                            {s.floorNumber != null && (
                              <>
                                <span>•</span>
                                <span>Fl {s.floorNumber}</span>
                              </>
                            )}
                          </p>

                          {flags.length > 0 && (
                            <div className="flex items-center gap-1.5 mt-1.5">
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 bg-amber-50/80 border border-amber-200/80 rounded-md">
                                <AlertTriangle className="w-2.5 h-2.5 text-amber-500" />
                                {flags.length} field{flags.length !== 1 ? "s" : ""} to complete
                              </span>
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })
                )
              )}
            </div>
          </div>

          {/* ── RIGHT COLUMN: INSPECTION & EDIT PANEL ────────────────── */}
          <div className="md:col-span-7 lg:col-span-8 p-6 flex flex-col justify-between overflow-y-auto max-h-[680px]">
            {tab === "buildings" ? (
              activeBuilding ? (
                <div className="flex flex-col h-full justify-between space-y-6">
                  <div className="space-y-6">
                    {/* Card Header & Status Hero */}
                    <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                      <div className="flex items-center gap-3.5 min-w-0">
                        <div className="w-12 h-12 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shrink-0">
                          <Building2 className="w-6 h-6" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-base font-bold text-slate-900 truncate">
                              {activeBuilding.buildingName || activeBuilding.name || `Building #${activeBuilding.id}`}
                            </h3>
                            {isItemApproved(activeBuilding) ? (
                              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold text-xs shrink-0">
                                ✓ Approved & Synced
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-50 text-amber-700 border border-amber-200 font-semibold text-xs shrink-0">
                                Pending Approval
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1 truncate">
                            <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span className="truncate">
                              {activeBuilding.streetAddress || "No street address"}, {activeBuilding.city || "No city"}
                            </span>
                          </p>
                        </div>
                      </div>

                      {!isItemApproved(activeBuilding) && (
                        <div className="flex items-center gap-2 shrink-0 self-end lg:self-auto">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={!hasBuildingChanges || saving[`building_${activeBuilding.id}`]}
                            onClick={() => handleSaveBuilding(activeBuilding.id)}
                            className="text-xs font-semibold whitespace-nowrap"
                          >
                            {saving[`building_${activeBuilding.id}`] ? (
                              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                            ) : (
                              <Check className="w-3.5 h-3.5 mr-1.5" />
                            )}
                            Save Edits
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            disabled={!isBuildingPromotable}
                            onClick={() => handleApproveBuildingWithAutoSave(activeBuilding.id)}
                            className={`text-xs font-semibold text-white whitespace-nowrap ${
                              isBuildingPromotable
                                ? "bg-emerald-600 hover:bg-emerald-700 shadow-sm"
                                : "bg-slate-300 cursor-not-allowed"
                            }`}
                          >
                            Approve Building
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Attention / Validation Alert */}
                    {!isItemApproved(activeBuilding) && buildingFlags.length > 0 && (
                      <div className="p-3.5 bg-rose-50/70 border border-rose-200/80 rounded-xl flex items-start gap-3">
                        <AlertTriangle className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
                        <div className="text-xs text-rose-800">
                          <span className="font-bold">Items requiring attention before sync: </span>
                          {buildingFlags.map(f => VALIATION_LABELS[f] || f).join(", ")}
                        </div>
                      </div>
                    )}

                    {/* Grouped Information Cards */}
                    <div className="space-y-4">
                      {/* Section 1: Google Maps Address Auto-fill & Search */}
                      {!isItemApproved(activeBuilding) && (
                        <div className="bg-gradient-to-r from-blue-50/70 via-indigo-50/40 to-slate-50 p-4 rounded-2xl border border-blue-150 space-y-2.5">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-2">
                              <Navigation className="w-4 h-4 text-blue-600" />
                              <h4 className="text-xs font-bold text-slate-800">
                                Auto-fill Address with Google Places & Map
                              </h4>
                            </div>
                            <Badge variant="outline" className="text-[10px] bg-white border-blue-200 text-blue-700 font-medium">
                              Auto-populates address, city, timezone & map pin
                            </Badge>
                          </div>
                          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                            <div className="flex-1">
                              <GooglePlacesSearchInput
                                placeholder="Search property, landmark or address on Google Maps…"
                                showSelectedBadge={false}
                                onPlaceSelected={place => {
                                  const resolvedCountry = resolveCountryCode(place.country, place.countryCode, countries);
                                  handleBuildingBatchEdit(activeBuilding.id, {
                                    buildingName: place.buildingName || activeBuilding.buildingName || activeBuilding.name,
                                    streetAddress: place.streetAddress || activeBuilding.streetAddress,
                                    city: place.city || activeBuilding.city,
                                    region: place.region || activeBuilding.region,
                                    country: resolvedCountry || place.countryCode || place.country || activeBuilding.country,
                                    postalCode: place.postalCode || activeBuilding.postalCode,
                                    digitalAddress: place.digitalAddress || place.postalCode || activeBuilding.digitalAddress,
                                    ...(place.timezone ? { timezone: place.timezone } : {}),
                                    latitude: place.latitude ?? activeBuilding.latitude,
                                    longitude: place.longitude ?? activeBuilding.longitude,
                                  });
                                  toastUtils.success("Address & location auto-filled from Google Maps");
                                }}
                              />
                            </div>
                            <LocationPickerModal
                              value={{
                                latitude: buildingEdits[activeBuilding.id]?.latitude ?? activeBuilding.latitude,
                                longitude: buildingEdits[activeBuilding.id]?.longitude ?? activeBuilding.longitude,
                                address: buildingEdits[activeBuilding.id]?.streetAddress ?? activeBuilding.streetAddress,
                              }}
                              onChange={loc => {
                                const resolvedCountry = resolveCountryCode(loc.country, loc.countryCode, countries);
                                handleBuildingBatchEdit(activeBuilding.id, {
                                  latitude: loc.latitude,
                                  longitude: loc.longitude,
                                  ...(loc.streetAddress || loc.address ? { streetAddress: loc.streetAddress || loc.address } : {}),
                                  ...(loc.city ? { city: loc.city } : {}),
                                  ...(loc.region ? { region: loc.region } : {}),
                                  ...(resolvedCountry || loc.countryCode || loc.country ? { country: resolvedCountry || loc.countryCode || loc.country } : {}),
                                  ...(loc.postalCode ? { postalCode: loc.postalCode } : {}),
                                  ...(loc.digitalAddress || loc.postalCode ? { digitalAddress: loc.digitalAddress || loc.postalCode } : {}),
                                  ...(loc.timezone ? { timezone: loc.timezone } : {}),
                                  ...(loc.name && !activeBuilding.buildingName && !activeBuilding.name ? { buildingName: loc.name } : {}),
                                });
                                toastUtils.success("Location & address updated from map");
                              }}
                              trigger={
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-10 text-xs font-semibold text-indigo-700 border-indigo-200 bg-white hover:bg-indigo-50 shrink-0 gap-1.5 shadow-xs"
                                >
                                  <MapPin className="w-4 h-4 text-indigo-600" />
                                  Pick on Map
                                </Button>
                              }
                            />
                          </div>
                        </div>
                      )}

                      {/* Section 2: Property Identity & Street Address */}
                      <div className="bg-white p-5 rounded-2xl border border-slate-200/80 space-y-4">
                        <div className="flex items-center gap-2 border-b border-slate-100 pb-2.5">
                          <Info className="w-4 h-4 text-indigo-600" />
                          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                            Property Identity
                          </h4>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="sm:col-span-2 space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Building / Property Name *</Label>
                            <Input
                              disabled={isItemApproved(activeBuilding)}
                              value={buildingEdits[activeBuilding.id]?.buildingName ?? activeBuilding.buildingName ?? activeBuilding.name ?? ""}
                              onChange={e => handleBuildingEdit(activeBuilding.id, "buildingName", e.target.value)}
                              placeholder="e.g., Apex Business Tower"
                              className="text-xs"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Street Address *</Label>
                            <Input
                              disabled={isItemApproved(activeBuilding)}
                              value={buildingEdits[activeBuilding.id]?.streetAddress ?? activeBuilding.streetAddress ?? ""}
                              onChange={e => handleBuildingEdit(activeBuilding.id, "streetAddress", e.target.value)}
                              placeholder="e.g., 45 Independence Avenue"
                              className="text-xs"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">City *</Label>
                            <Input
                              disabled={isItemApproved(activeBuilding)}
                              value={buildingEdits[activeBuilding.id]?.city ?? activeBuilding.city ?? ""}
                              onChange={e => handleBuildingEdit(activeBuilding.id, "city", e.target.value)}
                              placeholder="e.g., Nairobi / Accra"
                              className="text-xs"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Section 3: Regional & Geolocation Controls */}
                      <div className="bg-white p-5 rounded-2xl border border-slate-200/80 space-y-4">
                        <div className="flex items-center gap-2 border-b border-slate-100 pb-2.5">
                          <Compass className="w-4 h-4 text-indigo-600" />
                          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                            Region, Country & Geolocation
                          </h4>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Region / State</Label>
                            <Input
                              disabled={isItemApproved(activeBuilding)}
                              value={buildingEdits[activeBuilding.id]?.region ?? activeBuilding.region ?? ""}
                              onChange={e => handleBuildingEdit(activeBuilding.id, "region", e.target.value)}
                              placeholder="e.g., Greater Accra / Nairobi County"
                              className="text-xs"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Country</Label>
                            {countries && countries.length > 0 ? (() => {
                              const rawVal = String(buildingEdits[activeBuilding.id]?.country ?? activeBuilding.country ?? "").trim();
                              const matched = countries.find(c => {
                                const cCode = String(c.code || c.iso_code || c.iso2 || "").trim().toUpperCase();
                                const cName = String(c.name || "").trim().toLowerCase();
                                return (
                                  (cCode && cCode === rawVal.toUpperCase()) ||
                                  (cName && cName === rawVal.toLowerCase())
                                );
                              });
                              const currentVal = matched ? (matched.code || matched.iso_code || matched.iso2 || matched.name) : rawVal;

                              return (
                                <Select
                                  disabled={isItemApproved(activeBuilding)}
                                  value={currentVal || undefined}
                                  onValueChange={v => handleBuildingEdit(activeBuilding.id, "country", v)}
                                >
                                  <SelectTrigger className="text-xs">
                                    <SelectValue placeholder="Select Country" />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-60">
                                    {countries.map(c => {
                                      const val = c.code || c.iso_code || c.iso2 || c.name;
                                      return (
                                        <SelectItem key={c.id || val} value={val}>
                                          {c.name} {val ? `(${val})` : ""}
                                        </SelectItem>
                                      );
                                    })}
                                    {currentVal && !countries.some(c => (c.code || c.iso_code || c.iso2 || c.name) === currentVal) && (
                                      <SelectItem value={currentVal}>
                                        {currentVal}
                                      </SelectItem>
                                    )}
                                  </SelectContent>
                                </Select>
                              );
                            })() : (
                              <Input
                                disabled={isItemApproved(activeBuilding)}
                                value={buildingEdits[activeBuilding.id]?.country ?? activeBuilding.country ?? ""}
                                onChange={e => handleBuildingEdit(activeBuilding.id, "country", e.target.value)}
                                placeholder="e.g. Kenya, Ghana, Nigeria"
                                className="text-xs"
                              />
                            )}
                          </div>

                          <div className="space-y-1.5">
                            <TimezoneSelect
                              value={buildingEdits[activeBuilding.id]?.timezone ?? activeBuilding.timezone}
                              onChange={tz => handleBuildingEdit(activeBuilding.id, "timezone", tz)}
                              label="Timezone *"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Digital / Postal Code</Label>
                            <Input
                              disabled={isItemApproved(activeBuilding)}
                              value={buildingEdits[activeBuilding.id]?.digitalAddress ?? activeBuilding.digitalAddress ?? activeBuilding.postalCode ?? ""}
                              onChange={e => handleBuildingEdit(activeBuilding.id, "digitalAddress", e.target.value)}
                              placeholder="e.g., 00100 or Plus Code"
                              className="text-xs"
                            />
                          </div>

                          {/* Map Location & Pin Status */}
                          <div className="sm:col-span-2 pt-3 border-t border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-slate-50/70 p-3.5 rounded-xl border border-slate-200/70">
                            {(() => {
                              const lat = buildingEdits[activeBuilding.id]?.latitude ?? activeBuilding.latitude;
                              const lng = buildingEdits[activeBuilding.id]?.longitude ?? activeBuilding.longitude;
                              const hasCoords =
                                lat !== null &&
                                lat !== undefined &&
                                lng !== null &&
                                lng !== undefined &&
                                (Number(lat) !== 0 || Number(lng) !== 0);
                              const currentAddress =
                                buildingEdits[activeBuilding.id]?.streetAddress ?? activeBuilding.streetAddress ?? "";
                              const currentCity = buildingEdits[activeBuilding.id]?.city ?? activeBuilding.city ?? "";

                              return (
                                <>
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                      <MapPin className={`w-4 h-4 ${hasCoords ? "text-emerald-600" : "text-amber-500"}`} />
                                      <span className="text-xs font-bold text-slate-800">
                                        Map Location *
                                      </span>
                                      {hasCoords ? (
                                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] font-medium gap-1">
                                          <Check className="w-3 h-3 text-emerald-600 inline" />
                                          Verified on Map
                                        </Badge>
                                      ) : (
                                        <Badge
                                          variant="outline"
                                          className="text-amber-700 bg-amber-50 border-amber-200 text-[10px] font-medium"
                                        >
                                          Pin Required
                                        </Badge>
                                      )}
                                    </div>
                                    {hasCoords ? (
                                      <p className="text-[11px] text-slate-600">
                                        {currentAddress || currentCity ? (
                                          <span>
                                            Pinned for <span className="font-semibold text-slate-800">{[currentAddress, currentCity].filter(Boolean).join(", ")}</span>. Exact location is saved for directions.
                                          </span>
                                        ) : (
                                          "Property location pinned on map. Saved for navigation and check-in."
                                        )}
                                      </p>
                                    ) : (
                                      <p className="text-[11px] text-amber-700 font-medium">
                                        No map location pinned yet. Use the map or Google search above to set the property's location.
                                      </p>
                                    )}
                                  </div>

                                  <div className="flex items-center gap-2 shrink-0">
                                    <LocationPickerModal
                                      value={{
                                        latitude: lat,
                                        longitude: lng,
                                        address: currentAddress,
                                      }}
                                      onChange={loc => {
                                        const resolvedCountry = resolveCountryCode(loc.country, loc.countryCode, countries);
                                        handleBuildingBatchEdit(activeBuilding.id, {
                                          latitude: loc.latitude,
                                          longitude: loc.longitude,
                                          ...(loc.streetAddress || loc.address ? { streetAddress: loc.streetAddress || loc.address } : {}),
                                          ...(loc.city ? { city: loc.city } : {}),
                                          ...(loc.region ? { region: loc.region } : {}),
                                          ...(resolvedCountry || loc.countryCode || loc.country ? { country: resolvedCountry || loc.countryCode || loc.country } : {}),
                                          ...(loc.postalCode ? { postalCode: loc.postalCode } : {}),
                                          ...(loc.digitalAddress || loc.postalCode ? { digitalAddress: loc.digitalAddress || loc.postalCode } : {}),
                                          ...(loc.timezone ? { timezone: loc.timezone } : {}),
                                          ...(loc.name && !activeBuilding.buildingName && !activeBuilding.name ? { buildingName: loc.name } : {}),
                                        });
                                        toastUtils.success("Location updated from map");
                                      }}
                                      trigger={
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          className="h-8 text-xs font-semibold text-indigo-700 border-indigo-200 bg-white hover:bg-indigo-50 shadow-xs"
                                        >
                                          <MapPin className="w-3.5 h-3.5 mr-1 text-indigo-600" />
                                          {hasCoords ? "Change Pin on Map" : "Pin on Map"}
                                        </Button>
                                      }
                                    />

                                    {hasCoords && (
                                      <a
                                        href={`https://www.google.com/maps?q=${lat},${lng}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center text-xs font-medium text-slate-600 hover:text-indigo-600 transition-colors px-2 py-1 rounded hover:bg-slate-200/60"
                                      >
                                        Open Maps ↗
                                      </a>
                                    )}
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer Flow Actions */}
                  <div className="pt-4 border-t border-slate-200 flex items-center justify-between">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleSelectNext}
                      className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                    >
                      Next Building / Move to Spaces <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Button>

                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setTab("spaces")}
                      className="text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      Continue to Spaces Review →
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center p-12">
                  <Building2 className="w-10 h-10 text-slate-300 mb-2" />
                  <p className="text-xs font-bold text-slate-600">Select a building from the left</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">Inspect and verify address details before syncing</p>
                </div>
              )
            ) : (
              activeSpace ? (
                <div className="flex flex-col h-full justify-between space-y-6">
                  <div className="space-y-6">
                    {/* Hero Preview Card */}
                    <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                      <div className="flex items-center gap-3.5 min-w-0">
                        <div className="w-12 h-12 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                          <Layers className="w-6 h-6" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-base font-bold text-slate-900 truncate">
                              {activeSpace.name || `Space #${activeSpace.id}`}
                            </h3>
                            {isItemApproved(activeSpace) ? (
                              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold text-xs shrink-0">
                                ✓ Approved & Live
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-50 text-amber-700 border border-amber-200 font-semibold text-xs shrink-0">
                                Pending Approval
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-slate-700">
                              {activeSubcategory ? `${activeSubcategory.categoryName} · ${activeSubcategory.name}` : "General Space"}
                            </span>
                            <span>•</span>
                            <span>{activeSpace.capacity ? `${activeSpace.capacity} seats` : "Capacity open"}</span>
                            <span>•</span>
                            <span className="font-semibold text-indigo-600">
                              {activeSpace.ratePrice && activeSpace.currency
                                ? `${activeSpace.currency} ${activeSpace.ratePrice} / ${activeSpace.duration || "hour"}`
                                : "Price unconfigured"}
                            </span>
                          </p>
                        </div>
                      </div>

                      {!isItemApproved(activeSpace) && (
                        <div className="flex flex-col items-end gap-2 shrink-0 self-end lg:self-auto">
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={!hasSpaceChanges || saving[`space_${activeSpace.id}`]}
                              onClick={() => handleSaveSpace(activeSpace.id)}
                              className="text-xs font-semibold whitespace-nowrap"
                            >
                              {saving[`space_${activeSpace.id}`] ? (
                                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                              ) : (
                                <Check className="w-3.5 h-3.5 mr-1.5" />
                              )}
                              Save Edits
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              disabled={!isSpacePromotable}
                              onClick={() => handleApproveSpaceWithAutoSave(activeSpace.id)}
                              className={`text-xs font-semibold text-white whitespace-nowrap ${
                                isSpacePromotable
                                  ? "bg-emerald-600 hover:bg-emerald-700 shadow-sm"
                                  : "bg-slate-300 cursor-not-allowed"
                              }`}
                            >
                              Approve & Publish
                            </Button>
                          </div>
                          <label className="flex items-center gap-1.5 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={publishOnApprove}
                              onChange={e => setPublishOnApprove(e.target.checked)}
                              className="w-3.5 h-3.5 accent-emerald-600 cursor-pointer"
                            />
                            <span className="text-[11px] font-medium text-slate-600">
                              Publish publicly on approval
                            </span>
                          </label>
                        </div>
                      )}
                    </div>

                    {/* Parent building not yet approved — warn admin that space approval is blocked */}
                    {(() => {
                      const parentBuilding = buildings.find(b =>
                        b.id === activeSpace.stagedBuildingId || b.id === activeSpace.buildingId
                      );
                      if (!parentBuilding || isItemApproved(parentBuilding)) return null;
                      const parentName = parentBuilding.buildingName || parentBuilding.name || `Building #${parentBuilding.id}`;
                      return (
                        <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                          <div className="text-xs text-amber-900 flex-1">
                            <span className="font-bold block mb-0.5">Building not approved yet</span>
                            <span className="text-amber-800">
                              This space belongs to <span className="font-semibold">{parentName}</span>, which is still pending approval.
                              Approve the building first before approving its spaces.
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                setTab("buildings");
                                setSelectedBuildingId(parentBuilding.id);
                              }}
                              className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 hover:text-amber-900 hover:underline"
                            >
                              <Building2 className="w-3 h-3" />
                              Go to {parentName} →
                            </button>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Attention / Validation Alert */}
                    {spaceFlags.length > 0 && (
                      <div className="p-3.5 bg-rose-50/70 border border-rose-200/80 rounded-xl flex items-start gap-3">
                        <AlertTriangle className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
                        <div className="text-xs text-rose-800">
                          <span className="font-bold">Required to publish this space: </span>
                          {spaceFlags.map(f => VALIATION_LABELS[f] || f).join(", ")}
                        </div>
                      </div>
                    )}

                    {/* Form Sections */}
                    <div className="space-y-4">
                      {/* Section 1: Space Profile */}
                      <div className="bg-white p-5 rounded-2xl border border-slate-200/80 space-y-4">
                        <div className="flex items-center gap-2 border-b border-slate-100 pb-2.5">
                          <Tag className="w-4 h-4 text-indigo-600" />
                          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                            Space Profile & Category
                          </h4>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Space Name *</Label>
                            <Input
                              disabled={isItemApproved(activeSpace)}
                              value={spaceEdits[activeSpace.id]?.name ?? activeSpace.name ?? ""}
                              onChange={e => handleSpaceEdit(activeSpace.id, "name", e.target.value)}
                              placeholder="e.g., Executive Boardroom A"
                              className="text-xs"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Parent Building</Label>
                            <Select
                              disabled={isItemApproved(activeSpace)}
                              value={String(spaceEdits[activeSpace.id]?.stagedBuildingId ?? activeSpace.stagedBuildingId ?? "")}
                              onValueChange={v => handleSpaceEdit(activeSpace.id, "stagedBuildingId", Number(v))}
                            >
                              <SelectTrigger className="text-xs">
                                <SelectValue placeholder="Assign Building" />
                              </SelectTrigger>
                              <SelectContent>
                                {buildings.map(b => (
                                  <SelectItem key={b.id} value={String(b.id)}>
                                    {b.buildingName || b.name || `Building #${b.id}`} {b.city ? `(${b.city})` : ""}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Category / Subcategory *</Label>
                            <Select
                              disabled={isItemApproved(activeSpace)}
                              value={String(spaceEdits[activeSpace.id]?.subCategoryId ?? activeSpace.subCategoryId ?? "")}
                              onValueChange={v => handleSpaceEdit(activeSpace.id, "subCategoryId", Number(v))}
                            >
                              <SelectTrigger className="text-xs">
                                <SelectValue placeholder="Select Category" />
                              </SelectTrigger>
                              <SelectContent className="max-h-72">
                                {structuredCategories.map(category => (
                                  <SelectGroup key={category.id}>
                                    <SelectLabel className="text-[11px] uppercase tracking-wider text-slate-400 font-bold px-2 py-1">
                                      {category.name}
                                    </SelectLabel>
                                    {(category.subcategories || []).map(sub => (
                                      <SelectItem key={sub.id} value={String(sub.id)}>
                                        {sub.name}
                                      </SelectItem>
                                    ))}
                                  </SelectGroup>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="sm:col-span-3 space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Description</Label>
                            <Textarea
                              rows={2}
                              disabled={isItemApproved(activeSpace)}
                              value={spaceEdits[activeSpace.id]?.description ?? activeSpace.description ?? ""}
                              onChange={e => handleSpaceEdit(activeSpace.id, "description", e.target.value)}
                              placeholder="Describe what this space offers to members..."
                              className="text-xs"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Section 2: Media & Gallery Strip */}
                      {(activeSpace.images?.length > 0 || activeSpace.videos?.length > 0) && (
                        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 space-y-3">
                          <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
                            <div className="flex items-center gap-2">
                              <ImageIcon className="w-4 h-4 text-indigo-600" />
                              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                                Imported Media & Visuals
                              </h4>
                            </div>
                            <span className="text-[11px] text-slate-400 font-medium">
                              {activeSpace.images?.length || 0} photo{(activeSpace.images?.length || 0) === 1 ? "" : "s"}
                              {activeSpace.videos?.length ? ` · ${activeSpace.videos.length} video(s)` : ""}
                            </span>
                          </div>

                          <div className="flex items-center gap-3 overflow-x-auto pb-1.5 scrollbar-thin">
                            {activeSpace.images?.map((imgUrl, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => setPreviewImageUrl(imgUrl)}
                                className="relative group shrink-0 w-24 h-20 rounded-xl overflow-hidden border border-slate-200 bg-slate-100 shadow-2xs cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              >
                                <img
                                  src={imgUrl}
                                  alt={`Space asset ${i + 1}`}
                                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                                  onError={e => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                                <div
                                  className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white"
                                  title="View photo"
                                >
                                  <Eye className="w-5 h-5 drop-shadow" />
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Section 3: Pricing & Booking Controls */}
                      <div className="bg-white p-5 rounded-2xl border border-slate-200/80 space-y-4">
                        <div className="flex items-center gap-2 border-b border-slate-100 pb-2.5">
                          <DollarSign className="w-4 h-4 text-emerald-600" />
                          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                            Pricing, Specs & Booking Controls
                          </h4>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Rate Price *</Label>
                            <Input
                              type="number"
                              disabled={isItemApproved(activeSpace)}
                              value={spaceEdits[activeSpace.id]?.ratePrice ?? activeSpace.ratePrice ?? ""}
                              onChange={e => handleSpaceEdit(activeSpace.id, "ratePrice", Number(e.target.value))}
                              placeholder="0.00"
                              className="text-xs"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Currency *</Label>
                            <Select
                              disabled={isItemApproved(activeSpace)}
                              value={spaceEdits[activeSpace.id]?.currency ?? activeSpace.currency ?? ""}
                              onValueChange={v => handleSpaceEdit(activeSpace.id, "currency", v)}
                            >
                              <SelectTrigger className="text-xs">
                                <SelectValue placeholder="Currency" />
                              </SelectTrigger>
                              <SelectContent>
                                {CURRENCIES.map(c => (
                                  <SelectItem key={c} value={c}>
                                    {c}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Duration Unit</Label>
                            <Select
                              disabled={isItemApproved(activeSpace)}
                              value={spaceEdits[activeSpace.id]?.duration ?? activeSpace.duration ?? "HOURLY"}
                              onValueChange={v => handleSpaceEdit(activeSpace.id, "duration", v)}
                            >
                              <SelectTrigger className="text-xs">
                                <SelectValue placeholder="Duration" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="HOURLY">HOURLY</SelectItem>
                                <SelectItem value="DAILY">DAILY</SelectItem>
                                <SelectItem value="WEEKLY">WEEKLY</SelectItem>
                                <SelectItem value="MONTHLY">MONTHLY</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Available Units</Label>
                            <Input
                              type="number"
                              disabled={isItemApproved(activeSpace)}
                              value={spaceEdits[activeSpace.id]?.instanceCount ?? activeSpace.instanceCount ?? "1"}
                              onChange={e => handleSpaceEdit(activeSpace.id, "instanceCount", Number(e.target.value))}
                              placeholder="1"
                              className="text-xs"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Min Duration (hrs/days)</Label>
                            <Input
                              type="number"
                              disabled={isItemApproved(activeSpace)}
                              value={spaceEdits[activeSpace.id]?.minimumDuration ?? activeSpace.minimumDuration ?? ""}
                              onChange={e => handleSpaceEdit(activeSpace.id, "minimumDuration", Number(e.target.value))}
                              placeholder="1"
                              className="text-xs"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Max Duration (hrs/days)</Label>
                            <Input
                              type="number"
                              disabled={isItemApproved(activeSpace)}
                              value={spaceEdits[activeSpace.id]?.maximumDuration ?? activeSpace.maximumDuration ?? ""}
                              onChange={e => handleSpaceEdit(activeSpace.id, "maximumDuration", Number(e.target.value))}
                              placeholder="24"
                              className="text-xs"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Capacity (Seats)</Label>
                            <Input
                              type="number"
                              disabled={isItemApproved(activeSpace)}
                              value={spaceEdits[activeSpace.id]?.capacity ?? activeSpace.capacity ?? ""}
                              onChange={e => handleSpaceEdit(activeSpace.id, "capacity", Number(e.target.value))}
                              placeholder="e.g., 6"
                              className="text-xs"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Floor Number</Label>
                            <Input
                              type="number"
                              disabled={isItemApproved(activeSpace)}
                              value={spaceEdits[activeSpace.id]?.floorNumber ?? activeSpace.floorNumber ?? ""}
                              onChange={e => handleSpaceEdit(activeSpace.id, "floorNumber", Number(e.target.value))}
                              placeholder="e.g., 2"
                              className="text-xs"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Space Size</Label>
                            <Input
                              disabled={isItemApproved(activeSpace)}
                              value={spaceEdits[activeSpace.id]?.size ?? activeSpace.size ?? ""}
                              onChange={e => handleSpaceEdit(activeSpace.id, "size", e.target.value)}
                              placeholder="e.g. 30sqm"
                              className="text-xs"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Buffer Time (mins)</Label>
                            <Input
                              type="number"
                              disabled={isItemApproved(activeSpace)}
                              value={spaceEdits[activeSpace.id]?.bufferTimeInMinutes ?? activeSpace.bufferTimeInMinutes ?? ""}
                              onChange={e => handleSpaceEdit(activeSpace.id, "bufferTimeInMinutes", Number(e.target.value))}
                              placeholder="15"
                              className="text-xs"
                            />
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-slate-700">Space Type</Label>
                            <Input
                              disabled={isItemApproved(activeSpace)}
                              value={spaceEdits[activeSpace.id]?.spaceType ?? activeSpace.spaceType ?? ""}
                              onChange={e => handleSpaceEdit(activeSpace.id, "spaceType", e.target.value)}
                              placeholder="e.g. STANDARD"
                              className="text-xs"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Section 4: Operating Hours & Schedule */}
                      {((activeSpace.availabilitySlots && activeSpace.availabilitySlots.length > 0) || activeSpace.eventSchedule) && (
                        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 space-y-3">
                          <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-indigo-600" />
                              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                                Operating Hours & Schedule
                              </h4>
                            </div>
                            {activeSpace.eventSchedule?.recurring && (
                              <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px] font-semibold">
                                Recurring: {activeSpace.eventSchedule.recurrenceType || "Weekly"}
                              </Badge>
                            )}
                          </div>

                          {activeSpace.availabilitySlots && activeSpace.availabilitySlots.length > 0 ? (
                            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
                              {activeSpace.availabilitySlots.map((slot, idx) => (
                                <div
                                  key={idx}
                                  className="p-2.5 rounded-xl border border-slate-200/80 bg-slate-50/60 text-center flex flex-col justify-center"
                                >
                                  <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                                    {slot.day?.slice(0, 3)}
                                  </span>
                                  <span className="text-[11px] font-semibold text-indigo-600 mt-0.5">
                                    {slot.startTime} - {slot.endTime}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : activeSpace.eventSchedule ? (
                            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/70 text-xs text-slate-600 flex items-center justify-between">
                              <span>Event Time: <strong className="text-slate-800">{activeSpace.eventSchedule.startTime} - {activeSpace.eventSchedule.endTime}</strong></span>
                              {activeSpace.eventSchedule.eventDate && (
                                <span>Date: <strong className="text-slate-800">{activeSpace.eventSchedule.eventDate}</strong></span>
                              )}
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Footer Flow Actions */}
                  <div className="pt-4 border-t border-slate-200 flex items-center justify-between">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setTab("buildings")}
                      className="text-xs font-semibold text-slate-600 hover:text-slate-800"
                    >
                      ← Back to Buildings Review
                    </Button>

                    <div className="flex items-center gap-2">
                      {/* Mirrors the header action. Approving is the last thing an operator does on a
                          space and Next Space is the first thing they do after — keeping the pair
                          together saves a scroll back to the top of a long form on every space. */}
                      {!isItemApproved(activeSpace) && (
                        <Button
                          type="button"
                          size="sm"
                          disabled={!isSpacePromotable}
                          onClick={() => handleApproveSpaceWithAutoSave(activeSpace.id)}
                          className={`text-xs font-semibold text-white whitespace-nowrap ${
                            isSpacePromotable
                              ? "bg-emerald-600 hover:bg-emerald-700 shadow-sm"
                              : "bg-slate-300 cursor-not-allowed"
                          }`}
                        >
                          Approve & Publish
                        </Button>
                      )}

                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleSelectNext}
                        disabled={!nextSpace}
                        title={nextSpace ? undefined : "No further spaces in this building"}
                        className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 disabled:text-slate-300 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                      >
                        Next Space <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center p-12">
                  <ListChecks className="w-10 h-10 text-slate-300 mb-2" />
                  <p className="text-xs font-bold text-slate-600">Select a space from the left</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">Review pricing, capacity, and subcategory settings</p>
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* ── Image Lightbox Modal ────────────────────────────────────── */}
      {previewImageUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-150"
          onClick={() => setPreviewImageUrl(null)}
        >
          <div
            className="relative max-w-3xl max-h-[85vh] bg-slate-900 rounded-2xl overflow-hidden shadow-2xl flex flex-col items-center justify-center p-2"
            onClick={e => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPreviewImageUrl(null)}
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/60 hover:bg-black/90 text-white flex items-center justify-center transition-colors shadow-md"
              title="Close preview"
            >
              <X className="w-4 h-4" />
            </button>
            <img
              src={previewImageUrl}
              alt="Space preview"
              className="max-h-[80vh] w-auto max-w-full rounded-xl object-contain"
            />
          </div>
        </div>
      )}

      {/* ── Re-fetch Confirmation Dialog ────────────────────────────── */}
      <Dialog open={showRebackfillModal} onOpenChange={open => !rebackfilling && setShowRebackfillModal(open)}>
        <DialogContent className="z-[70] sm:max-w-md bg-white border border-slate-200/80 shadow-2xl rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0 text-indigo-600">
              <RefreshCw className="w-5 h-5" />
            </div>
            <div className="space-y-1.5 flex-1 pr-6">
              <DialogTitle className="text-base font-semibold text-slate-900">
                Re-fetch spaces from host?
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500 leading-relaxed">
                This will query the connected host platform again and refresh your staging review data. Any new listings or modifications from the host will be synchronized into your staging review.
              </DialogDescription>
            </div>
          </div>

          <div className="mt-2 rounded-xl bg-slate-50 border border-slate-100 p-3 text-[11px] text-slate-500 flex items-center gap-2">
            <Info className="w-4 h-4 text-slate-400 shrink-0" />
            <span>
              Published spaces are never overwritten — if the host has changed one, it is listed here
              for you to apply.
            </span>
          </div>

          <DialogFooter className="flex items-center justify-end gap-2.5 pt-4 mt-2 border-t border-slate-100">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={rebackfilling}
              onClick={() => setShowRebackfillModal(false)}
              className="text-xs font-medium text-slate-600 hover:text-slate-900 border-slate-200"
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={rebackfilling}
              onClick={handleRebackfill}
              className="text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
            >
              {rebackfilling ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                  Re-fetch spaces
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StagingReview;
